import { ref } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';

export function usePoReference() {
    const working = ref(false);

    const escapeHtml = (value) => String(value).replace(
        /[&<>"']/g,
        (character) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        })[character]
    );

    const confirmThenSetReference = (payloads, done = () => {}) => {
        if (working.value) {
            return;
        }

        const jobs = Array.isArray(payloads) ? payloads : [payloads];
        const references = jobs.map((job) => `<strong>${escapeHtml(job.reference)}</strong>`).join(', ');
        const count = jobs.length;

        Swal.fire({
            icon: 'question',
            title: `Set PO and invoice ${count} ${count === 1 ? 'job' : 'jobs'}?`,
            html: `<p class="mb-2">This records a supplied purchase order against ${references}, `
                + 'then raises and emails the invoice immediately.</p>',
            input: 'text',
            inputLabel: 'PO reference',
            inputPlaceholder: 'Enter the customer purchase order reference',
            inputAttributes: {
                maxlength: 255,
                autocomplete: 'off',
            },
            showCancelButton: true,
            confirmButtonText: 'Set PO and invoice',
            cancelButtonText: 'Cancel',
            width: '650px',
            preConfirm: (value) => {
                const reference = String(value ?? '').trim();

                if (reference.length === 0) {
                    Swal.showValidationMessage('Enter a purchase order reference.');
                    return false;
                }

                return reference;
            },
        }).then((result) => {
            if (result.isConfirmed) {
                process(jobs, result.value, done);
            }
        });
    };

    const process = (jobs, purchaseOrder, done) => {
        working.value = true;

        const data = new FormData();
        data.append('ids', jobs.map((job) => job.id).join(','));
        data.append('purchase_order', purchaseOrder);

        axios.post(route('api.movements.po.set-reference'), data)
            .then((response) => reportOutcome(response.data, done))
            .catch((error) => {
                working.value = false;
                reportError(error);
            });
    };

    const reportOutcome = (result, done) => {
        working.value = false;

        const assigned = result.assigned ?? 0;
        const skipped = result.skipped ?? 0;
        const failed = result.failed ?? 0;
        const messages = result.messages ?? [];
        const detail = messages.length > 0
            ? `<ul class="text-start small mb-0">${messages.map((message) => `<li>${escapeHtml(message)}</li>`).join('')}</ul>`
            : '';

        Swal.fire({
            icon: failed > 0 ? 'error' : (skipped > 0 ? 'warning' : 'success'),
            title: 'PO assignment complete',
            html: `<p class="mb-2"><strong>${assigned}</strong> assigned and invoiced, `
                + `<strong>${skipped}</strong> skipped, <strong>${failed}</strong> failed.</p>${detail}`,
            width: '700px',
            confirmButtonText: 'Close',
        }).then(done);
    };

    const reportError = (error) => {
        if (!error.response) {
            return;
        }

        let message = '';

        if (error.response?.data?.errors) {
            Object.values(error.response.data.errors).flat().forEach((item) => {
                message += `<p class="mb-0">${escapeHtml(item)}</p>`;
            });
        } else if (error.response?.data?.message) {
            message = escapeHtml(error.response.data.message);
        }

        Swal.fire({
            icon: 'error',
            title: 'Could not assign the purchase order',
            html: message,
            width: '600px',
            confirmButtonText: 'Dismiss',
            confirmButtonColor: '#dc3545',
        });
    };

    return {
        working,
        confirmThenSetReference,
    };
}
